<template>
  <section>
    <errors-list :errorMessage="errorMessage" />

    <v-data-table
      :headers="translatedHeaders"
      :items="items"
      :loading="loading"
      :rows-per-page-items="rowsPerPageItems"
      :total-items="pagination.totalItems"
      :pagination.sync="pagination"
      item-key="_id"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <template :header="header" :row="props.item" v-for="(header, index) in translatedHeaders">
            <td v-if="header.value === 'meta.employeeName'" :key="index">
              <span v-if="props.item.meta.employeeName">{{ props.item.meta.employeeName }}</span>
              <span v-else>{{ $t('@auditTrailEventsList/systemUserName') }}</span>
            </td>
            <grid-cell v-else :header="header" :row="props.item" :key="index"></grid-cell>
          </template>
        </tr>
      </template>
    </v-data-table>
  </section>
</template>

<script>
import ErrorsList from '../../components/ErrorsList';
import GridCell from '../../components/GridCell';
import { getErrorMessage } from '../../errorHandlers';
import { defaultDateTimeFormatter, serializeQuery } from '../../utils';

export const defaultHeaders = [
  {
    text: '@auditTrailEventsList/createdAtColumnHeader',
    value: 'createdAt',
    format: defaultDateTimeFormatter,
    sortable: false,
  },
  {
    text: '@auditTrailEventsList/messageColumnHeader',
    value: 'message',
    sortable: false,
    format: (value, row) => value || `Event of type "${row.type}"`,
  },
  {
    text: '@auditTrailEventsList/wasColumnHeader',
    value: 'obj.old',
    sortable: false,
    format: (value) => value || '-',
  },
  {
    text: '@auditTrailEventsList/nowColumnHeader',
    value: 'obj.new',
    sortable: false,
    format: (value) => value || '-',
  },
  {
    text: '@auditTrailEventsList/userColumnHeader',
    value: 'meta.employeeName',
    sortable: false,
  },
];

const employeeIdToName = {};
const visibleEventTypes = [
  'InvoiceApproved',
  'InvoiceDeclined',
  'BankDetailsApproved',
  'BankDetailsDeclined',
  'InvoiceInvalidAccount',
  'InvoiceInsufficientFunds',
  'InvoiceMandateNotFound',
];

export default {
  components: { GridCell, ErrorsList },

  props: {
    filters: {
      type: Object,
      default: () => ({ customerId: 0 }),
    },
    headers: {
      type: Array,
      default: () => defaultHeaders,
    },
  },

  watch: {
    filters() {
      this.fetchData();
    },

    pagination() {
      this.fetchData();
    },
  },

  data() {
    return {
      lastQueryString: '',
      errorMessage: '',
      loading: false,
      items: [],
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 0,
      },
      rowsPerPageItems: [10, 15, 50, 100],
    };
  },

  computed: {
    translatedHeaders() {
      return Array.isArray(this.headers)
        ? this.headers.map((h) => ({
            ...h,
            text: this.$t(h.text),
          }))
        : [];
    },

    query() {
      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const take = this.pagination.rowsPerPage;
      const query = {
        query: JSON.stringify({
          'meta.customerId': Number(this.filters.customerId),
        }),
        // include: 'old new',
        skip,
        take,
        sortBy: '-registeredAt',
      };

      query.from = this.filters.from || new Date(-1);

      return query;
    },
  },

  methods: {
    async fetchData() {
      const { take } = this.query;
      const qs = serializeQuery(this.query);

      if (this.lastQueryString === qs || this.loading || !this.filters.customerId) {
        return;
      }

      try {
        this.loading = true;
        let { data } = await this.$http.get(`/api/employee/current/event?${qs}`);
        const events = data.data.filter((x) => visibleEventTypes.includes(x.type));

        for (const event of events) {
          await this.augmentEmployeeName(event);
        }

        this.items = events || [];
        this.lastQueryString = qs;

        if (events.length === Number(take)) {
          this.pagination.totalItems = data.meta.total;
          this.pagination.skip = take;
        }

        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },

    async augmentEmployeeName(event) {
      if (!event.meta || !event.meta.employeeId) return;

      try {
        let name = employeeIdToName[event.meta.employeeId];

        if (!name) {
          const { data } = await this.$http.get(`/api/employees/${event.meta.employeeId}`);
          name = `${data.firstName} ${data.lastName}`;
          employeeIdToName[event.meta.employeeId] = name;
        }

        event.meta.employeeName = name;
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      }
    },
  },
};
</script>

<style scoped>
>>> .v-datatable__actions__pagination {
  display: none;
}
code {
  width: 100%;
}
</style>
